import { Injectable, NgModule } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterModule, RouterStateSnapshot, Routes, UrlTree } from '@angular/router';
import { UnauthorizedComponent } from '@prisma/unauthorized/unauthorized.component';
import { NoRoleComponent } from '@prisma/no-role/no-role.component';
import { hasUserIdentity } from '@prisma/no-role/user-identity';
import { NgxPermissionsGuard } from 'ngx-permissions';
import { APP_EMPTY_PATH_REDIRECT_TO_DEFAULT_ROUTE } from './app.config';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { DynReportComponent } from '@prisma/components/dyn-report/dyn-report.component';
import { _isDev, _isFeDev, _isLab, _isStaging, _log } from '@shared/aux_helper_environment';
import { Observable, of } from 'rxjs';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { map } from 'rxjs/operators';
import { byPassPermissionsAccessModule } from './services/permissions-manager.service';
import { PlanogramsAccessLoaderComponent } from './public/planograms-access/planograms-access-loader.component';
import { TempAccessSessionService } from './services/temp-access-session.service';
const byPassPermissions = byPassPermissionsAccessModule;

@Injectable()
export class PrismaCanActivateRoute implements CanActivate {
  constructor(
    private oidcSecurityService: OidcSecurityService,
    private tempAccessSession: TempAccessSessionService,
    private router: Router
  ) {}

  /*
  Custom canActivate // se fija si el usuario tiene algún rol, si no, se usa para deshabilitar las rutas DES-486
  */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.tempAccessSession.isValid()) {
      const isAllowedGuestRoute = this.tempAccessSession.isAllowedGuestRoute(state?.url);
      if (isAllowedGuestRoute) return of(true);

      return this.router.parseUrl('/unauthorized');
    }

    return this.oidcSecurityService.getUserData().pipe(
      map(data => {
        let hasRole = Boolean(data?.role?.length) || _isDev();

        if (!hasRole && (_isDev() || _isLab())) console.warn('user has no role');

        // PLATFORM-SEC03 (F02/F03): antes devolvia `false` y la navegacion se cancelaba SIN redirigir,
        // dejando al usuario sin rol en pantalla en blanco. Ahora aterriza en la pantalla informativa.
        // Se mantiene intacto el vaciado de menu de DES-486: esto agrega el aterrizaje que le faltaba.
        // Sin identidad (pantalla de login) no hay rol que evaluar: se conserva el `false` original.
        if (!hasRole) return hasUserIdentity(data) ? this.router.parseUrl('/no-role') : false;

        return true;
      })
    );
  }
}

const routes: Routes = [
  APP_EMPTY_PATH_REDIRECT_TO_DEFAULT_ROUTE,
  {
    // Acceso público por código temporal (invitados)
    path: 'planograms-access/:code',
    component: PlanogramsAccessLoaderComponent,
  },
  {
    path: 'home',
    loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
  },
  {
    path: 'data-entry',
    loadChildren: () => import('./data-entry-adaptations/data-entry-adaptations.module').then(m => m.DataEntryAdaptationsModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
  },
  {
    path: 'prices',
    loadChildren: () => import('./prices/prices.module').then(m => m.PricesModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['prices_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'assortment',
    loadChildren: () => import('./assortment/assortment.module').then(m => m.AssortmentModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['assortment_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'assortment-v2',
    loadChildren: () => import('./assortment-v2/assortment-v2.module').then(m => m.AssortmentV2Module),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['range_assortment_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'supply',
    loadChildren: () => import('./supply/supply.module').then(m => m.SupplyModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['vendor_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'admin',
    loadChildren: () => import('./admin/admin-core.module').then(m => m.AdminCoreModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['admin_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'mystery-shopper',
    loadChildren: () => import('./mystery-shopper/mystery-shopper.module').then(m => m.MysteryShopperModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['mysteryShopper_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'item-master',
    loadChildren: () => import('./item-master-v2/item-masterv2.module').then(m => m.ItemMasterV2Module),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['itemmaster_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'ai-chat',
    loadChildren: () => import('./ai-chat/ai-chat.module').then(m => m.AiChatModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions ? {} : {},
  },
  {
    path: 'ai-dashboard',
    loadChildren: () => import('./ai-dashboard/ai-dashboard.module').then(m => m.AiDashboardModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions ? {} : {},
  },
  {
    path: 'promotionsV2',
    loadChildren: () => import('./promotionsv2/promotionsv2.module').then(m => m.PromotionsV2Module),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['promotion_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'reports/general-reports',
    loadChildren: () => import('./prisma/components/prisma-reports-v2/prisma-reports-v2.module').then(m => m.PrismaReportsV2Module),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
  },
  {
    path: 'report/:id',
    component: DynReportComponent,
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
  },
  {
    path: 'report/:id/:params',
    component: DynReportComponent,
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
  },
  {
    path: 'page-not-found',
    component: PageNotFoundComponent,
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    // PLATFORM-SEC03: aterrizaje del usuario sin rol. SIN guards a proposito: PrismaCanActivateRoute
    // redirige aca, asi que guardarla generaria un ciclo de redireccion.
    path: 'no-role',
    component: NoRoleComponent,
  },
  {
    path: 'admin/master/stores',
    loadChildren: () => import('./admin/master/master.module').then(m => m.MasterModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['storemaster_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'spaces',
    loadChildren: () => import('./spaces/spaces.module').then(m => m.SpacesModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: byPassPermissions
      ? {}
      : {
          permissions: {
            only: ['spaces_view'],
            redirectTo: {
              default: '/unauthorized',
            },
          },
        },
  },
  {
    path: 'test',
    loadChildren: () => import('./test/test.module').then(m => m.TestModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
    data: {
      permissions: {
        only: ['not_show'],
        redirectTo: {
          default: '/unauthorized',
        },
      },
    },
  },
  {
    path: 'selling',
    loadChildren: () => import('./selling/selling.module').then(m => m.SellingModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
  },
  {
    path: 'helps',
    loadChildren: () => import('./helps/helps.module').then(m => m.HelpsModule),
    canActivate: [NgxPermissionsGuard, PrismaCanActivateRoute],
  },
  {
    path: '**',
    redirectTo: 'page-not-found',
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: true,
      relativeLinkResolution: 'legacy',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
